import { Navigate, Route, Routes, NavLink, useLocation } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import { Landmark, LayoutDashboard, Table2, Puzzle, FileSearch } from 'lucide-react';
import { lazy, Suspense } from 'react';

// Lazy-load heavy pages
const AssetDashboard = lazy(() => import('./AssetDashboard'));
const AssetRegistry = lazy(() => import('./AssetRegistry'));
const AssetBuilder = lazy(() => import('./AssetBuilder'));
const AssetDetailPage = lazy(() => import('./AssetDetailPage'));

const NAV_ITEMS = [
  { label: 'Dashboard', path: '', icon: LayoutDashboard },
  { label: 'Registry', path: 'registry', icon: Table2 },
  { label: 'Builder', path: 'builder', icon: Puzzle },
] as const;

function NavBar() {
  const location = useLocation();
  // Determine active tab from pathname
  const base = '/asset';
  const sub = location.pathname.replace(base, '').replace(/^\//, '').split('/')[0] || '';

  return (
    <div className="flex items-center gap-1 px-6 pt-5 pb-2">
      {/* Brand */}
      <div className="flex items-center gap-2 mr-6">
        <div className="w-8 h-8 rounded-lg bg-amber-600 flex items-center justify-center">
          <Landmark className="w-4 h-4 text-white" />
        </div>
        <span className="text-lg font-semibold text-gray-900">Asset App</span>
      </div>

      {/* Tab links */}
      <nav className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
        {NAV_ITEMS.map((item) => {
          const isActive = sub === item.path || (item.path === '' && sub === '');
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path === '' ? base : `${base}/${item.path}`}
              end={item.path === ''}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                ${isActive
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}
              `}
            >
              <Icon className="w-3.5 h-3.5" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

function FallbackSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
    </div>
  );
}

export default function AssetRouter() {
  return (
    <div className="page-asset h-full flex flex-col">
      <NavBar />
      <div className="flex-1 min-h-0 px-6 pb-6 overflow-auto">
        <Suspense fallback={<FallbackSpinner />}>
          <Routes>
            <Route index element={<AssetDashboard />} />
            <Route path="registry" element={<AssetRegistry />} />
            <Route
              path="builder"
              element={
                <ReactFlowProvider>
                  <AssetBuilder />
                </ReactFlowProvider>
              }
            />
            <Route path=":id" element={<AssetDetailPage />} />
            <Route path="*" element={<Navigate to="/asset" replace />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}
